import { Component } from "./component";
import aiLogger from './ai-logger';
import { readSSEStream } from '../services/ai-stream-reader';
import { getCsrfToken, escapeHtml, findEditorIframe, fixCodeBlockEscapes } from '../services/ai-editor-utils';
import { VtbRegistry, createVtbRegistry } from '../wysiwyg-visual-tools';

/**
 * Tipo para uma categoria de componentes retornada pela API.
 */
interface ComponentCategory {
    tag: string;
    label: string;
    components: ComponentItem[];
}

interface ComponentItem {
    hash: string;
    tag: string;
    description: string;
    examples: Record<string, string>;
    preview_html: string;
}

/**
 * ComponentLibraryModal â€” Modal de biblioteca de componentes HTML.
 *
 * Fluxo do botÃ£o flutuante:
 * 1. Aguarda o iframe do editor estar disponÃ­vel (polling cada 500ms)
 * 2. Escuta selectionchange + mouseup + keyup no iframe
 * 3. Calcula a posiÃ§Ã£o Y do cursor e posiciona o botÃ£o externo ao editor
 * 4. mousedown no botÃ£o: e.preventDefault() preserva o foco + salva DOM Range
 * 5. Modal abre â†’ usuÃ¡rio escolhe/ajusta componente â†’ clica Inserir
 * 6. InserÃ§Ã£o: restaura iframe focus + DOM range â†’ emite editor::insert
 */
export class ComponentLibraryModal extends Component {
    protected modal: any = null;
    protected modalEl: HTMLElement | null = null;
    protected initialized: boolean = false;
    protected categories: ComponentCategory[] = [];
    protected activeTab: string = '__all__';
    protected selectedComponent: ComponentItem | null = null;
    protected abortController: AbortController | null = null;
    protected renderedTabs: Set<string> = new Set();

    // Lazy loading
    protected readonly PAGE_SIZE: number = 6; // 2 colunas Ã— 3 linhas
    protected lazyItems: ComponentItem[] = [];
    protected lazyOffset: number = 0;
    protected lazyObserver: IntersectionObserver | null = null;

    // Floating button state
    protected floatBtn: HTMLElement | null = null;
    protected editorIframe: HTMLIFrameElement | null = null;
    protected savedRange: Range | null = null;
    protected cursorVisible: boolean = false;

    // Modo de ediÃ§Ã£o: guarda o nodeKey do nÃ³ a ser substituÃ­do (null = inserÃ§Ã£o normal)
    protected pendingNodeKey: string | null = null;

    // DOM refs
    protected browseState: HTMLElement | null = null;
    protected editorState: HTMLElement | null = null;
    protected tabsContainer: HTMLElement | null = null;
    protected gridContainer: HTMLElement | null = null;
    protected previewEl: HTMLElement | null = null;
    protected htmlEditor: HTMLTextAreaElement | null = null;
    protected exampleSelect: HTMLSelectElement | null = null;
    protected aiPromptInput: HTMLTextAreaElement | null = null;
    protected aiAdjustBtn: HTMLElement | null = null;
    protected aiLoading: HTMLElement | null = null;
    protected backBtn: HTMLElement | null = null;
    protected insertBtn: HTMLElement | null = null;
    protected headerTitle: HTMLElement | null = null;
    protected visualEditor: HTMLElement | null = null;
    protected activeEditTab: string = 'html';
    // HTML original do botÃ£o de inserÃ§Ã£o (restaurado ao sair do modo ediÃ§Ã£o)
    protected insertBtnOriginalHtml: string = '';

    // â”€â”€ Smart Visual Editor state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    protected vtbRegistry!: VtbRegistry;

    setup() {
        // Escuta evento legado (toolbar) â€” mantido por compatibilidade
        window.$events.listen('ai-component-library::open', () => {
            this.pendingNodeKey = null;
            this.openModal();
        });

        // Abre a biblioteca em modo de EDIÃ‡ÃƒO de componente existente (duplo-clique no bloco)
        window.$events.listen<{ html: string; nodeKey: string; componentTag?: string }>('ai-component-library::open-with-html', (payload: { html: string; nodeKey: string; componentTag?: string }) => {
            if (!payload || !payload.nodeKey || !payload.html) return;
            this.pendingNodeKey = payload.nodeKey;
            this.openModalWithHtml(payload.html, payload.componentTag || null);
        });

        // Abre a biblioteca em modo BROWSE jÃ¡ filtrada na categoria do componente
        window.$events.listen<{ componentTag?: string | null }>('ai-component-library::open-browse', (payload: { componentTag?: string | null }) => {
            this.pendingNodeKey = null;
            this.openModalBrowse(payload ? payload.componentTag || null : null);
        });

        // Instala o botÃ£o flutuante quando o editor estiver disponÃ­vel
        this.waitForEditorAndInstall();
    }

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // FLOATING BUTTON â€” detecta cursor e posiciona botÃ£o externo ao editor
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    protected waitForEditorAndInstall(): void {
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            if (attempts > 60) { clearInterval(interval); return; }

            const iframe = this.findEditorIframe();
            if (iframe && iframe.contentDocument) {
                clearInterval(interval);
                this.editorIframe = iframe;
                this.createFloatBtn();
                this.installCursorTracker(iframe);
            }
        }, 500);
    }

    protected findEditorIframe(): HTMLIFrameElement | null {
        return findEditorIframe();
    }

    protected createFloatBtn(): void {
        this.floatBtn = document.createElement('div');
        this.floatBtn.className = 'cl-float-btn';
        this.floatBtn.setAttribute('title', 'Inserir componente da biblioteca');
        this.floatBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M4 11h5V5H4zm0 7h5v-6H4zm6 0h5v-6h-5zm6 0h5v-6h-5zm-6-7h5V5h-5zm6-6v6h5V5z"/></svg><span>Componentes</span>`;
        this.floatBtn.style.display = 'none';

        // mousedown com preventDefault: preserva foco no iframe antes do click
        this.floatBtn.addEventListener('mousedown', (e: MouseEvent) => {
            e.preventDefault();
            this.saveCurrentRange();
            this.openModal();
        });

        document.body.appendChild(this.floatBtn);
    }

    protected installCursorTracker(iframe: HTMLIFrameElement): void {
        const doc = iframe.contentDocument!;
        const update = () => setTimeout(() => this.updateFloatBtnPosition(), 50);

        doc.addEventListener('mouseup', update);
        doc.addEventListener('keyup', update);
        doc.addEventListener('selectionchange', update);

        // Salva range imediatamente no mousedown (antes do click processar)
        doc.addEventListener('mousedown', () => {
            setTimeout(() => this.saveCurrentRange(), 0);
        });

        // Oculta quando o iframe perde foco (usuÃ¡rio clicou fora do editor)
        if (iframe.contentWindow) {
            iframe.contentWindow.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.isModalOpen()) {
                        this.hideFloatBtn();
                    }
                }, 150);
            });
        }
    }

    protected saveCurrentRange(): void {
        const doc = this.editorIframe && this.editorIframe.contentDocument;
        if (!doc) return;
        const sel = doc.getSelection();
        if (sel && sel.rangeCount > 0) {
            try {
                this.savedRange = sel.getRangeAt(0).cloneRange();
            } catch (e) {
                // Ignorar erros de range invÃ¡lido
            }
        }
    }

    protected updateFloatBtnPosition(): void {
        if (!this.floatBtn || !this.editorIframe) return;

        const doc = this.editorIframe.contentDocument;
        if (!doc) return;

        const sel = doc.getSelection();
        if (!sel || sel.rangeCount === 0) {
            this.hideFloatBtn();
            return;
        }

        const range = sel.getRangeAt(0);
        let rect = range.getBoundingClientRect();

        // rect vazio = cursor em local nÃ£o mensurÃ¡vel â€” usa o elemento pai
        if (!rect || (rect.width === 0 && rect.height === 0 && rect.top === 0)) {
            const node = range.startContainer;
            const el = (node.nodeType === Node.ELEMENT_NODE)
                ? node as Element
                : (node as Text).parentElement;
            if (!el) { this.hideFloatBtn(); return; }
            rect = el.getBoundingClientRect();
            if (!rect || rect.top === 0) { this.hideFloatBtn(); return; }
        }

        this.positionFloatBtn(rect);
        this.saveCurrentRange();
    }

    protected positionFloatBtn(rect: DOMRect): void {
        if (!this.floatBtn || !this.editorIframe) return;

        const iframeRect = this.editorIframe.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;

        // Top: alinhado ao centro vertical da linha do cursor (relativo Ã  pÃ¡gina)
        const top = iframeRect.top + rect.top + (rect.height / 2) + scrollY;
        // Left: Ã  esquerda da borda do iframe
        const left = iframeRect.left - 4;

        this.floatBtn.style.top = `${top}px`;
        this.floatBtn.style.left = `${left}px`;
        this.floatBtn.style.display = 'flex';
        this.cursorVisible = true;
    }

    protected hideFloatBtn(): void {
        if (this.floatBtn) {
            this.floatBtn.style.display = 'none';
        }
        this.cursorVisible = false;
    }

    protected isModalOpen(): boolean {
        if (!this.modal || !this.modalEl) return false;
        const style = window.getComputedStyle(this.modalEl);
        return style.display !== 'none' && style.visibility !== 'hidden';
    }

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // MODAL INIT + LIFECYCLE
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    protected initModalIfNeeded(): boolean {
        if (this.initialized) return true;

        try {
            this.modalEl = document.getElementById('ai-component-library-modal');
            if (!this.modalEl) return false;

            this.modal = window.$components.firstOnElement(this.modalEl, 'popup');
            if (!this.modal) return false;

            this.browseState = this.modalEl.querySelector('#ai-cl-browse-state');
            this.editorState = this.modalEl.querySelector('#ai-cl-editor-state');
            this.tabsContainer = this.modalEl.querySelector('#ai-cl-tabs');
            this.gridContainer = this.modalEl.querySelector('#ai-cl-grid');
            this.previewEl = this.modalEl.querySelector('#ai-cl-preview');
            this.htmlEditor = this.modalEl.querySelector('#ai-cl-html-editor') as HTMLTextAreaElement;
            this.exampleSelect = this.modalEl.querySelector('#ai-cl-example-select') as HTMLSelectElement;
            this.aiPromptInput = this.modalEl.querySelector('#ai-cl-ai-prompt') as HTMLTextAreaElement;
            this.aiAdjustBtn = this.modalEl.querySelector('#ai-cl-ai-adjust-btn');
            this.aiLoading = this.modalEl.querySelector('#ai-cl-ai-loading');
            this.backBtn = this.modalEl.querySelector('#ai-cl-back-btn');
            this.insertBtn = this.modalEl.querySelector('#ai-cl-insert-btn');
            this.headerTitle = this.modalEl.querySelector('#ai-cl-header-title');
            this.visualEditor = this.modalEl.querySelector('#ai-cl-visual-editor');

            // Salva o HTML original do botÃ£o (contÃ©m o SVG do @icon('check'))
            if (this.insertBtn) {
                this.insertBtnOriginalHtml = this.insertBtn.innerHTML;
            }

            this.initVtbRegistry();
            this.setupListeners();
            this.initialized = true;
            return true;
        } catch (e) {
            aiLogger.error('[ComponentLibrary] Init failed:', e);
            return false;
        }
    }

    protected setupListeners(): void {
        this.backBtn && this.backBtn.addEventListener('click', () => this.showBrowseState());
        this.insertBtn && this.insertBtn.addEventListener('click', () => this.insertIntoEditor());
        this.aiAdjustBtn && this.aiAdjustBtn.addEventListener('click', () => this.adjustWithAI());

        // Ctrl+Enter no textarea da IA dispara ajuste
        this.aiPromptInput && this.aiPromptInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); this.adjustWithAI(); }
        });

        this.htmlEditor && this.htmlEditor.addEventListener('input', () => this.updatePreviewFromEditor());
        this.exampleSelect && this.exampleSelect.addEventListener('change', () => this.loadSelectedExample());

        // Smart visual editor
        this.setupSmartEditor();

        // Tabs HTML | Renderizado
        if (this.modalEl) {
            const editTabs = this.modalEl.querySelectorAll('.ai-cl-edit-tab');
            for (let i = 0; i < editTabs.length; i++) {
                const tab = editTabs[i] as HTMLElement;
                tab.addEventListener('click', () => this.switchEditTab(tab.dataset.editTab || 'html'));
            }
        }

        // Ao editar no visual editor, sincroniza preview + textarea em tempo real
        this.visualEditor && this.visualEditor.addEventListener('input', () => {
            if (this.htmlEditor && this.visualEditor) {
                this.htmlEditor.value = this.visualEditor.innerHTML;
            }
            if (this.previewEl && this.visualEditor) {
                this.previewEl.innerHTML = this.visualEditor.innerHTML;
            }
        });
    }

    protected openModal(): Promise<void> {
        if (!this.initModalIfNeeded()) {
            window.$events.emit('error', 'Erro ao abrir biblioteca de componentes.');
            return Promise.resolve();
        }

        this.showBrowseState();
        this.modal.show();

        if (this.categories.length === 0) {
            return this.loadComponents();
        }

        return Promise.resolve();
    }

    /**
     * Abre o modal em modo BROWSE jÃ¡ filtrado na categoria do componente.
     * Se componentTag for fornecida, carrega as categorias (se necessÃ¡rio)
     * e alterna para a tab correspondente.
     */
    protected openModalBrowse(componentTag: string | null): void {
        if (!this.initModalIfNeeded()) {
            window.$events.emit('error', 'Erro ao abrir biblioteca de componentes.');
            return;
        }

        // Restaura o botÃ£o de inserÃ§Ã£o original (caso tenha sido modificado por editBlock)
        if (this.insertBtn && this.insertBtnOriginalHtml) {
            this.insertBtn.innerHTML = this.insertBtnOriginalHtml;
        }
        if (this.headerTitle) this.headerTitle.textContent = 'Biblioteca de Componentes';

        this.showBrowseState();
        this.modal.show();

        const switchToTag = () => {
            if (componentTag && this.categories.length > 0) {
                const normalized = componentTag.replace(/-/g, '_');
                for (let i = 0; i < this.categories.length; i++) {
                    if (this.categories[i].tag === normalized) {
                        this.switchTab(normalized);
                        return;
                    }
                }
            }
        };

        if (this.categories.length === 0) {
            this.loadComponents().then(() => switchToTag());
        } else {
            switchToTag();
        }
    }

    /**
     * Abre o modal em modo de EDIÃ‡ÃƒO: pula o browse e vai direto ao editor
     * com o HTML do nÃ³ existente prÃ©-carregado.
     * Se componentTag for fornecida, tenta detectar o componente na biblioteca
     * para popular o dropdown de exemplos.
     */
    protected openModalWithHtml(html: string, componentTag: string | null = null): void {
        if (!this.initModalIfNeeded()) {
            window.$events.emit('error', 'Erro ao abrir biblioteca de componentes.');
            return;
        }

        // PrÃ©-carrega o HTML no editor do modal
        if (this.htmlEditor) this.htmlEditor.value = html;
        this.updatePreviewFromEditor();

        // Tenta detectar o componente na biblioteca para popular exemplos
        const matched = this.matchComponentByTag(componentTag);
        if (matched) {
            this.selectedComponent = matched;
            // Popula o dropdown de exemplos
            if (this.exampleSelect) {
                this.exampleSelect.innerHTML = '';
                const keys = Object.keys(matched.examples || {});
                for (let i = 0; i < keys.length; i++) {
                    const opt = document.createElement('option');
                    opt.value = keys[i];
                    opt.textContent = this.formatExampleKey(keys[i]);
                    this.exampleSelect.appendChild(opt);
                }
            }
            // Atualiza o tÃ­tulo com o nome do componente
            if (this.headerTitle) {
                let catLabel = matched.tag;
                for (let i = 0; i < this.categories.length; i++) {
                    if (this.categories[i].tag === matched.tag) {
                        catLabel = this.categories[i].label;
                        break;
                    }
                }
                this.headerTitle.textContent = 'Editar: ' + catLabel;
            }
        } else {
            if (this.headerTitle) this.headerTitle.textContent = 'Editar Componente';
        }

        if (this.insertBtn) this.insertBtn.textContent = '\u270E Atualizar';

        // Alterna para a aba Visual para ediÃ§Ã£o direta
        this.switchEditTab('visual');

        // Vai direto para o estado de ediÃ§Ã£o, sem passar pelo browse
        this.showEditorState();
        this.modal.show();

        if (this.categories.length === 0) {
            // Carrega componentes em background para as tabs do select
            this.loadComponents().then(() => {
                // Após carregar, tenta fazer o match novamente se não tinha dados
                if (!matched && componentTag) {
                    const retryMatch = this.matchComponentByTag(componentTag);
                    if (this.vtbRegistry) {
                        const tags = this.categories.map(cat => cat.tag);
                        this.vtbRegistry.addComponentSelectors(tags);
                        this.vtbRegistry.setCategories(this.categories);
                    }
                    if (retryMatch) {
                        this.selectedComponent = retryMatch;
                        if (this.exampleSelect) {
                            this.exampleSelect.innerHTML = '';
                            const keys = Object.keys(retryMatch.examples || {});
                            for (let i = 0; i < keys.length; i++) {
                                const opt = document.createElement('option');
                                opt.value = keys[i];
                                opt.textContent = this.formatExampleKey(keys[i]);
                                this.exampleSelect.appendChild(opt);
                            }
                        }
                    }
                }
            }).catch(() => { });
        }
    }

    /**
     * Busca um componente na biblioteca a partir da tag (ex: 'action_card').
     * Retorna o primeiro ComponentItem que pertence a essa categoria,
     * ou null se nÃ£o encontrar.
     */
    protected matchComponentByTag(tag: string | null): ComponentItem | null {
        if (!tag || this.categories.length === 0) return null;

        // Normaliza: a tag pode vir com underscore (DB) ou hyphen (CSS-class)
        const normalized = tag.replace(/-/g, '_');

        for (let i = 0; i < this.categories.length; i++) {
            const cat = this.categories[i];
            if (cat.tag === normalized && cat.components.length > 0) {
                return cat.components[0];
            }
        }

        return null;
    }

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // LOAD + RENDER
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    protected loadComponents(): Promise<void> {
        if (!this.gridContainer) return Promise.resolve();

        this.gridContainer.innerHTML = `
            <div class="ai-cl-loading">
                <div class="ai-generating-spinner"></div>
                <span>Carregando componentes...</span>
            </div>
        `;

        const csrfToken = getCsrfToken();

        return fetch('/ai/components', {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrfToken },
        }).then((resp) => {
            if (!resp.ok) throw new Error('HTTP ' + resp.status);
            return resp.json();
        }).then((data: any) => {
            if (!data.success || !Array.isArray(data.categories)) throw new Error('Resposta invÃ¡lida');
            this.categories = data.categories;
            this.renderTabs();
            this.renderGrid('__all__');
        }).catch((e: any) => {
            aiLogger.error('[ComponentLibrary] Load failed:', e);
            if (this.gridContainer) {
                this.gridContainer.innerHTML = `<div class="ai-cl-loading"><span class="text-neg">Erro: ${escapeHtml(String(e.message))}</span></div>`;
            }
        });
    }

    protected renderTabs(): void {
        if (!this.tabsContainer) return;
        this.tabsContainer.innerHTML = '';

        const allTab = document.createElement('button');
        allTab.type = 'button';
        allTab.className = 'ai-cl-tab active';
        allTab.dataset.tag = '__all__';
        allTab.textContent = 'Todos';
        allTab.addEventListener('click', () => this.switchTab('__all__'));
        this.tabsContainer.appendChild(allTab);

        for (let i = 0; i < this.categories.length; i++) {
            const cat = this.categories[i];
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'ai-cl-tab';
            tab.dataset.tag = cat.tag;
            tab.textContent = cat.label;
            tab.addEventListener('click', () => this.switchTab(cat.tag));
            this.tabsContainer.appendChild(tab);
        }
    }

    protected switchTab(tag: string): void {
        this.activeTab = tag;
        if (this.tabsContainer) {
            const tabs = this.tabsContainer.querySelectorAll('.ai-cl-tab');
            for (let i = 0; i < tabs.length; i++) {
                const t = tabs[i] as HTMLElement;
                t.classList.toggle('active', t.dataset.tag === tag);
            }
        }
        this.destroyLazyObserver();
        this.renderGrid(tag);
    }

    protected renderGrid(tag: string): void {
        if (!this.gridContainer) return;

        // Coletar todos os itens da aba
        let all: ComponentItem[] = [];
        if (tag === '__all__') {
            for (let i = 0; i < this.categories.length; i++) {
                const cat = this.categories[i];
                for (let j = 0; j < cat.components.length; j++) {
                    all.push(cat.components[j]);
                }
            }
        } else {
            for (let i = 0; i < this.categories.length; i++) {
                if (this.categories[i].tag === tag) {
                    all = this.categories[i].components;
                    break;
                }
            }
        }

        this.gridContainer.innerHTML = '';
        this.gridContainer.scrollTop = 0;

        if (all.length === 0) {
            this.gridContainer.innerHTML = `<div class="ai-cl-loading"><span>Nenhum componente encontrado.</span></div>`;
            return;
        }

        // Inicializar estado lazy
        this.lazyItems = all;
        this.lazyOffset = 0;

        // Renderizar primeiro lote
        this.appendNextBatch();
    }

    protected appendNextBatch(): void {
        if (!this.gridContainer) return;

        // Remove sentinela e spinner existentes
        this.gridContainer.querySelector('.ai-cl-sentinel')?.remove();
        this.gridContainer.querySelector('.ai-cl-lazy-spinner')?.remove();

        const end = Math.min(this.lazyOffset + this.PAGE_SIZE, this.lazyItems.length);
        const batch = this.lazyItems.slice(this.lazyOffset, end);

        for (let i = 0; i < batch.length; i++) {
            const comp = batch[i];
            const card = document.createElement('div');
            card.className = 'ai-cl-card';
            card.addEventListener('click', () => this.selectComponent(comp));

            const infoDiv = document.createElement('div');
            infoDiv.className = 'ai-cl-card-info';

            const humanTag = comp.tag.split('_')
                .map(s => s.charAt(0).toUpperCase() + s.slice(1))
                .join(' ');

            infoDiv.innerHTML = `
                <div class="ai-cl-card-tag">${escapeHtml(humanTag)}</div>
                <div class="ai-cl-card-desc">${escapeHtml(comp.description)}</div>
            `;

            card.appendChild(infoDiv);
            this.gridContainer.appendChild(card);
        }

        this.lazyOffset = end;

        // Se ainda hÃ¡ mais itens, adiciona sentinela para lazy load
        if (this.lazyOffset < this.lazyItems.length) {
            const spinner = document.createElement('div');
            spinner.className = 'ai-cl-lazy-spinner';
            spinner.innerHTML = `<div class="ai-generating-spinner" style="width:16px;height:16px;"></div><span>Carregando mais...</span>`;
            spinner.style.display = 'none';
            this.gridContainer.appendChild(spinner);

            const sentinel = document.createElement('div');
            sentinel.className = 'ai-cl-sentinel';
            this.gridContainer.appendChild(sentinel);

            this.setupLazyObserver(sentinel, spinner);
        }
    }

    protected setupLazyObserver(sentinel: HTMLElement, spinner: HTMLElement): void {
        this.destroyLazyObserver();

        this.lazyObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                spinner.style.display = 'flex';
                // Pequeno delay para animaÃ§Ã£o de spinner aparecer
                setTimeout(() => {
                    this.appendNextBatch();
                }, 120);
            }
        }, {
            root: this.gridContainer,
            rootMargin: '0px',
            threshold: 0.1,
        });

        this.lazyObserver.observe(sentinel);
    }

    protected destroyLazyObserver(): void {
        if (this.lazyObserver) {
            this.lazyObserver.disconnect();
            this.lazyObserver = null;
        }
    }

    protected selectComponent(comp: ComponentItem): void {
        this.clearSmartSelection();
        this.selectedComponent = comp;

        if (this.exampleSelect) {
            this.exampleSelect.innerHTML = '';
            const keys = Object.keys(comp.examples || {});
            for (let i = 0; i < keys.length; i++) {
                const opt = document.createElement('option');
                opt.value = keys[i];
                opt.textContent = this.formatExampleKey(keys[i]);
                this.exampleSelect.appendChild(opt);
            }
        }

        this.loadSelectedExample();

        if (this.headerTitle) {
            let catLabel = comp.tag;
            for (let i = 0; i < this.categories.length; i++) {
                if (this.categories[i].tag === comp.tag) { catLabel = this.categories[i].label; break; }
            }
            this.headerTitle.textContent = 'Componente: ' + catLabel;
        }

        this.showEditorState();
    }

    protected loadSelectedExample(): void {
        if (!this.selectedComponent || !this.exampleSelect) return;
        this.clearSmartSelection();
        const html = (this.selectedComponent.examples || {})[this.exampleSelect.value] || '';
        if (this.htmlEditor) this.htmlEditor.value = html;
        this.updatePreviewFromEditor();
    }

    protected updatePreviewFromEditor(): void {
        if (!this.previewEl || !this.htmlEditor) return;
        this.previewEl.innerHTML = this.htmlEditor.value;
        // Sincroniza aba visual se estiver visÃ­vel
        if (this.activeEditTab === 'visual' && this.visualEditor) {
            this.visualEditor.innerHTML = this.htmlEditor.value;
        }
    }

    protected formatExampleKey(key: string): string {
        // "com_migration" â†’ "Com migration"
        return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    }

    protected switchEditTab(tab: string): void {
        this.activeEditTab = tab;
        if (this.modalEl) {
            const editTabs = this.modalEl.querySelectorAll('.ai-cl-edit-tab');
            for (let i = 0; i < editTabs.length; i++) {
                const t = editTabs[i] as HTMLElement;
                t.classList.toggle('active', t.dataset.editTab === tab);
            }
        }
        const htmlPane = this.modalEl ? this.modalEl.querySelector('#ai-cl-pane-html') as HTMLElement : null;
        const visualPane = this.modalEl ? this.modalEl.querySelector('#ai-cl-pane-visual') as HTMLElement : null;
        const previewWrap = this.modalEl ? this.modalEl.querySelector('.ai-cl-preview-wrap') as HTMLElement : null;
        if (tab === 'html') {
            if (htmlPane) htmlPane.style.display = '';
            if (visualPane) visualPane.style.display = 'none';
            if (previewWrap) previewWrap.style.display = '';
            // Limpa seleção ao sair do visual
            this.clearSmartSelection();
        } else {
            if (htmlPane) htmlPane.style.display = 'none';
            if (visualPane) visualPane.style.display = '';
            // No modo visual o editor já é o preview — oculta o preview separado
            if (previewWrap) previewWrap.style.display = 'none';
            // Carrega HTML atual no editor visual
            if (this.visualEditor && this.htmlEditor) {
                this.visualEditor.innerHTML = this.htmlEditor.value;
            }
            // Reset toolbar para estado inicial
            this.clearSmartSelection();
        }
    }

    protected showBrowseState(): void {
        this.clearSmartSelection();
        if (this.browseState) this.browseState.style.display = '';
        if (this.editorState) this.editorState.style.display = 'none';
        if (this.headerTitle) this.headerTitle.textContent = 'Biblioteca de Componentes';
        // Restaura o label original do botÃ£o (com Ã­cone SVG do Blade)
        if (this.insertBtn && this.insertBtnOriginalHtml) {
            this.insertBtn.innerHTML = this.insertBtnOriginalHtml;
        }
        this.pendingNodeKey = null;
    }

    protected showEditorState(): void {
        if (this.browseState) this.browseState.style.display = 'none';
        if (this.editorState) this.editorState.style.display = '';
    }

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // INSERÃ‡ÃƒO NO CURSOR â€” restaura selection iframe antes de emitir
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    protected insertIntoEditor(): void {
        if (!this.htmlEditor) return;

        const html = this.htmlEditor.value.trim();
        if (!html) {
            window.$events.emit('warning', 'Nenhum HTML para inserir.');
            return;
        }

        // 1. Fechar o modal primeiro para liberar o foco
        if (this.modal) this.modal.hide();

        // â”€â”€ MODO EDIÃ‡ÃƒO: substituir nÃ³ existente â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (this.pendingNodeKey) {
            const nodeKeyToReplace = this.pendingNodeKey;
            this.pendingNodeKey = null;
            // Restaura o label/icon original do botÃ£o
            if (this.insertBtn && this.insertBtnOriginalHtml) {
                this.insertBtn.innerHTML = this.insertBtnOriginalHtml;
            }
            setTimeout(() => {
                window.$events.emit('editor::replace-node', { html, nodeKey: nodeKeyToReplace });
            }, 50);
            return;
        }

        // â”€â”€ MODO INSERÃ‡ÃƒO: inserir no cursor â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        // 2. Restaurar foco + seleÃ§Ã£o no iframe para que o Lexical saiba onde inserir
        const iframe = this.editorIframe || this.findEditorIframe();
        if (iframe && this.savedRange) {
            try {
                // Foca o contentWindow do iframe
                if (iframe.contentWindow) iframe.contentWindow.focus();

                // Restaura a seleÃ§Ã£o DOM no iframe
                const doc = iframe.contentDocument;
                if (doc) {
                    const sel = doc.getSelection();
                    if (sel) {
                        sel.removeAllRanges();
                        sel.addRange(this.savedRange);
                    }
                }
            } catch (e) {
                console.warn('[ComponentLibrary] Could not restore selection:', e);
            }
        }

        // â”€â”€ CE=false guard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        // Se o savedRange aponta para dentro de um bloco AI com contenteditable="false",
        // o TinyMCE nÃ£o consegue inserir conteÃºdo lÃ¡. Reposicionamos o cursor para o
        // FINAL desse bloco, permitindo a inserÃ§Ã£o.
        if (this.savedRange && iframe && iframe.contentDocument) {
            try {
                const doc = iframe.contentDocument;
                const anchor = this.savedRange.startContainer;
                let checkEl: HTMLElement | null = anchor.nodeType === Node.ELEMENT_NODE
                    ? anchor as HTMLElement
                    : (anchor as Text).parentElement;

                // Sobe na Ã¡rvore atÃ© encontrar um bloco AI com CE=false
                while (checkEl && checkEl !== doc.body) {
                    const ce = checkEl.getAttribute('contenteditable');
                    const isAiBlock = checkEl.hasAttribute('data-ai-comp-block')
                        || checkEl.hasAttribute('data-ai-comp-span');
                    if (ce === 'false' && isAiBlock) {
                        // Encontrou: reposiciona para o fim do conteÃºdo do bloco
                        const rng = doc.createRange();
                        rng.selectNodeContents(checkEl);
                        rng.collapse(false);
                        if (iframe.contentWindow) iframe.contentWindow.focus();
                        const sel = doc.getSelection();
                        if (sel) {
                            sel.removeAllRanges();
                            sel.addRange(rng);
                        }
                        this.savedRange = rng.cloneRange();
                        break;
                    }
                    checkEl = checkEl.parentElement;
                }
            } catch (e) {
                console.warn('[ComponentLibrary] CE=false guard failed:', e);
            }
        }

        // 3. Emitir insert com pequeno delay para deixar o foco se estabelecer
        const htmlToInsert = html;
        setTimeout(() => {
            window.$events.emit('editor::insert', { html: htmlToInsert });
        }, 50);
    }

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // AI ADJUST â€” ajuste inteligente via streaming
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    protected async adjustWithAI(): Promise<void> {
        if (!this.aiPromptInput || !this.htmlEditor) return;

        const instruction = this.aiPromptInput.value.trim();
        if (!instruction) { window.$events.emit('warning', 'Digite uma instruÃ§Ã£o para o ajuste.'); return; }

        const html = this.htmlEditor.value;
        if (!html) { window.$events.emit('warning', 'Nenhum HTML para ajustar.'); return; }

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        if (this.aiLoading) this.aiLoading.style.display = 'flex';
        if (this.aiAdjustBtn) this.aiAdjustBtn.setAttribute('disabled', 'true');
        if (this.aiPromptInput) this.aiPromptInput.disabled = true;

        try {
            const resp = await fetch('/ai/component-adjust', {
                method: 'POST',
                signal: this.abortController.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    html,
                    instruction,
                    component_hash: this.selectedComponent ? this.selectedComponent.hash : null,
                }),
            });

            if (!resp.ok) throw new Error('HTTP ' + resp.status);

            const fullContent = await readSSEStream(resp, {
                onContentChunk: (content: string) => {
                    if (this.htmlEditor) this.htmlEditor.value = fixCodeBlockEscapes(content.trim());
                    this.updatePreviewFromEditor();
                },
            });

            if (this.htmlEditor) this.htmlEditor.value = fixCodeBlockEscapes(fullContent);
            this.updatePreviewFromEditor();
            if (this.aiPromptInput) this.aiPromptInput.value = '';
            window.$events.emit('success', 'Componente ajustado!');
        } catch (err: any) {
            if (err && err.name === 'AbortError') return;
            window.$events.emit('error', 'Erro ao ajustar: ' + (err ? err.message : 'Desconhecido'));
        } finally {
            if (this.aiLoading) this.aiLoading.style.display = 'none';
            if (this.aiAdjustBtn) this.aiAdjustBtn.removeAttribute('disabled');
            if (this.aiPromptInput) this.aiPromptInput.disabled = false;
        }
    }

    // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
    // SMART VISUAL EDITOR â€” thin delegates â†’ wysiwyg-visual-tools/
    // Para adicionar novos plugins: crie plugins/xxx.ts + painel Blade + registre
    // em wysiwyg-visual-tools/index.ts. Zero alteraÃ§Ãµes aqui.
    // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

    protected initVtbRegistry(): void {
        this.vtbRegistry = createVtbRegistry();
        this.vtbRegistry.setRefs({
            modalEl: this.modalEl!,
            visualEditor: this.visualEditor!,
            htmlEditor: this.htmlEditor!,
            previewEl: this.previewEl!,
            miniModalEl: document.getElementById('vtb-mini-modal'),
        });
    }

    protected setupSmartEditor(): void {
        if (!this.visualEditor) return;
        this.vtbRegistry.init();
    }

    protected processSmartClick(target: HTMLElement): void {
        this.vtbRegistry.handleClick(target);
    }

    protected clearSmartSelection(): void {
        this.vtbRegistry.clearSelection();
    }

    protected syncEditorFromVisual(): void {
        this.vtbRegistry.sync();
    }
}

// â”€â”€â”€ REMOVIDO â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// O bloco "SMART VISUAL EDITOR" com ~470 linhas foi migrado para:
//   resources/js/wysiwyg-visual-tools/
//   â””â”€â”€ index.ts        â† createVtbRegistry() + ordem dos plugins
//   â””â”€â”€ registry.ts     â† VtbRegistry (detecÃ§Ã£o, toolbar, sync)
//   â””â”€â”€ types.ts        â† VtbPlugin, SmartCtx, helpers
//   â””â”€â”€ plugins/
//       â”œâ”€â”€ badge.ts, badges-group.ts, meta-field.ts, list-item.ts
//       â”œâ”€â”€ checklist-item.ts, alert.ts, endpoint.ts
//       â”œâ”€â”€ table-row.ts, deploy-box.ts
// Para adicionar um novo plugin: crie o .ts + painel Blade + 1 linha em index.ts
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// NOTE: this comment block intentionally left here to prevent accidental re-addition.
// DO NOT add plugin logic back to this file.
const _vtbMigratedSentinel = true; // eslint-disable-line @typescript-eslint/no-unused-vars
function _initVtbRefs_REMOVED(_q: unknown): void {
    throw new Error('initVtbRefs was migrated to wysiwyg-visual-tools. See comment above.');
}
if (false) { void _vtbMigratedSentinel; void _initVtbRefs_REMOVED; } // tree-shake
